import React, { useState, useRef, useEffect } from 'react';
import { Brain } from 'lucide-react';

interface SearchBarProps {
  onSearch: (query: string, deepMode?: boolean) => boolean | void;
  isLoading?: boolean;
  initialValue?: string;
  placeholderMessage?: string;
  autoFocus?: boolean;
  deleteOnSubmit?: boolean;
  className?: string;
  onValueChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onDeepModeToggle?: (enabled: boolean) => void;
  deepModeEnabled?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  isLoading = false, 
  initialValue = '', 
  placeholderMessage = '',
  autoFocus = false,
  deleteOnSubmit = false,
  className = '',
  onValueChange,
  onFocus,
  onBlur,
  onDeepModeToggle,
  deepModeEnabled = false,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [isMultiline, setIsMultiline] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [pressDuration, setPressDuration] = useState(0);
  const [isPressing, setIsPressing] = useState(false);
  const [deepModeActivated, setDeepModeActivated] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pressStartTimeRef = useRef<number | null>(null);
  const pressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const DEEP_MODE_THRESHOLD = 1500; // 1.5 seconds in milliseconds

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const scrollHeight = inputRef.current.scrollHeight;
      const maxHeight = 200; // Maximum height in pixels
      const newHeight = Math.min(scrollHeight, maxHeight);
      inputRef.current.style.height = `${newHeight}px`;
      
      // Update multiline state based on content
      setIsMultiline(scrollHeight > 40); // 40px is roughly the height of a single line
    }
  }, [inputValue]);

  // Cleanup press interval on unmount
  useEffect(() => {
    return () => {
      if (pressIntervalRef.current) {
        clearInterval(pressIntervalRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      const result = onSearch(inputValue.trim(), deepModeActivated);
      if (result === true && deleteOnSubmit) {
        setInputValue(''); // Clear the input after submission
      }
      // Reset press state after submission
      setDeepModeActivated(false);
      setPressDuration(0);
    }
  };

  const handlePressStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); // Prevent form submission on press
    if (isLoading || !inputValue.trim()) return;
    
    setIsPressing(true);
    setDeepModeActivated(false);
    setPressDuration(0);
    pressStartTimeRef.current = Date.now();

    // Update press duration every 50ms for smooth animation
    pressIntervalRef.current = setInterval(() => {
      if (pressStartTimeRef.current) {
        const elapsed = Date.now() - pressStartTimeRef.current;
        setPressDuration(elapsed);

        // Activate deep mode when threshold is reached
        if (elapsed >= DEEP_MODE_THRESHOLD && !deepModeActivated) {
          setDeepModeActivated(true);
          onDeepModeToggle?.(true);
        }
      }
    }, 50);
  };

  const handlePressEnd = (e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault(); // Prevent form submission
    }
    
    // Check if we were actually pressing (using ref to avoid race conditions)
    const wasPressing = pressStartTimeRef.current !== null;
    const shouldUseDeepMode = deepModeActivated;
    
    setIsPressing(false);
    if (pressIntervalRef.current) {
      clearInterval(pressIntervalRef.current);
      pressIntervalRef.current = null;
    }
    
    // If we were pressing and have valid input, trigger search
    if (wasPressing && inputValue.trim() && !isLoading) {
      const result = onSearch(inputValue.trim(), shouldUseDeepMode);
      if (result === true && deleteOnSubmit) {
        setInputValue('');
      }
    }
    
    pressStartTimeRef.current = null;
    
    // Reset after a short delay to show the final state
    setTimeout(() => {
      setDeepModeActivated(false);
      setPressDuration(0);
      // Only reset toggle if deep mode wasn't activated
      if (!shouldUseDeepMode) {
        onDeepModeToggle?.(false);
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onValueChange?.(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <form onSubmit={handleSubmit} className={`w-full ${className}`}>
      <div 
        ref={containerRef}
        className={`relative w-full transition-all duration-200 ${
          isMultiline ? 'max-w-5xl' : 'max-w-4xl'
        } mx-auto`}
      >
        <div className="flex items-start gap-2">
          <div 
            className={`relative flex-1 flex items-center transition-all duration-200 ${
              isMultiline ? 'min-h-[20px]' : 'h-12'
            }`}
            style={{
              backgroundColor: 'var(--rhea-bg-tertiary)',
              borderRadius: '12px',
              border: '1px solid var(--rhea-card-border)',
              boxShadow: isFocused ? '0 0 0 2px var(--rhea-accent-primary), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={`${placeholderMessage}`}
              className="w-full bg-transparent px-6 py-3 outline-none border-none focus:shadow resize-none"
              style={{
                color: 'var(--rhea-text-primary)',
                minHeight: '48px',
                maxHeight: '200px',
                borderRadius: '12px',
                overflowY: 'auto'
              }}
              rows={1}
            />
          </div>
          <button
            ref={buttonRef}
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
            className="px-4 rounded-lg hover:opacity-90 disabled:opacity-50 transition-all duration-200 flex items-center justify-center relative overflow-visible"
            style={{ 
              backgroundColor: 'var(--rhea-accent-primary)',
              color: 'var(--rhea-text-primary)',
              minWidth: '48px',
              minHeight: '56px',
            }}
          >
            {/* Icon animation container */}
            <div className="relative flex items-center justify-center w-8 h-8">
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {/* Send icon - always visible, moves to corner when brain is active */}
                  <svg 
                    className="absolute transition-all duration-300 ease-out"
                    style={{
                      width: (isPressing || deepModeEnabled) ? '16px' : '32px',
                      height: (isPressing || deepModeEnabled) ? '16px' : '32px',
                      opacity: (isPressing || deepModeEnabled) ? 0.5 : 1,
                      transform: (isPressing || deepModeEnabled) 
                        ? 'translate(10px, -10px) scale(0.5)' 
                        : 'translate(0, 0) scale(1)',
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 20 20"
                  >
                    <path d="M17.218,2.268L2.477,8.388C2.13,8.535,2.164,9.05,2.542,9.134L9.33,10.67l1.535,6.787c0.083,0.377,0.602,0.415,0.745,0.065l6.123-14.74C17.866,2.46,17.539,2.134,17.218,2.268 M3.92,8.641l11.772-4.89L9.535,9.909L3.92,8.641z M11.358,16.078l-1.268-5.613l6.157-6.157L11.358,16.078z"></path>
                  </svg>
                  
                  {/* Brain icon - shows when pressing or deep mode enabled */}
                  <div
                    className="absolute transition-all duration-300 ease-out"
                    style={{
                      opacity: (isPressing || deepModeEnabled) ? 1 : 0,
                      transform: (isPressing || deepModeEnabled)
                        ? `scale(${deepModeEnabled 
                            ? 1.2 
                            : 1 + Math.min(pressDuration / DEEP_MODE_THRESHOLD * 0.6, 0.6)})`
                        : 'scale(0.8)',
                      filter: (deepModeActivated || deepModeEnabled)
                        ? 'drop-shadow(0 0 6px rgba(99, 102, 241, 0.6)) drop-shadow(0 0 12px rgba(99, 102, 241, 0.4))' 
                        : 'none',
                      zIndex: 1,
                    }}
                  >
                    <Brain 
                      className="w-8 h-8 transition-colors duration-300"
                      style={{
                        color: (deepModeActivated || deepModeEnabled) 
                          ? 'rgb(99, 102, 241)' // indigo-500
                          : 'currentColor',
                      }}
                    />
                  </div>
                  
                  {/* Subtle glow rings when deep mode is active */}
                  {(deepModeActivated || deepModeEnabled) && (
                    <>
                      <div 
                        className="absolute inset-0 rounded-full border border-indigo-400/30"
                        style={{
                          width: '130%',
                          height: '130%',
                          top: '-15%',
                          left: '-15%',
                          animation: 'pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                        }}
                      />
                      <div 
                        className="absolute inset-0 rounded-full border border-indigo-400/20"
                        style={{
                          width: '150%',
                          height: '150%',
                          top: '-25%',
                          left: '-25%',
                          animation: 'pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          animationDelay: '0.8s',
                        }}
                      />
                    </>
                  )}
                  
                  {/* Subtle energy particles when approaching critical mass */}
                  {isPressing && pressDuration > DEEP_MODE_THRESHOLD * 0.6 && (
                    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-1 h-1 bg-indigo-400/60 rounded-full"
                          style={{
                            left: '50%',
                            top: '50%',
                            transform: `translate(-50%, -50%) rotate(${i * 90}deg) translateY(-18px)`,
                            opacity: Math.min((pressDuration - DEEP_MODE_THRESHOLD * 0.6) / (DEEP_MODE_THRESHOLD * 0.4), 0.8),
                            animation: deepModeActivated ? 'pulse 1s ease-in-out infinite' : 'none',
                            animationDelay: `${i * 0.15}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Subtle glow effect on button when pressing or deep mode enabled */}
            {(isPressing || deepModeEnabled) && (
              <div
                className="absolute inset-0 rounded-lg pointer-events-none transition-all duration-300"
                style={{
                  boxShadow: (deepModeActivated || deepModeEnabled)
                    ? `0 0 ${deepModeEnabled ? 20 : Math.min(pressDuration / DEEP_MODE_THRESHOLD * 25, 25)}px rgba(99, 102, 241, 0.3), inset 0 0 ${deepModeEnabled ? 15 : Math.min(pressDuration / DEEP_MODE_THRESHOLD * 15, 15)}px rgba(99, 102, 241, 0.15)`
                    : `0 0 ${Math.min(pressDuration / DEEP_MODE_THRESHOLD * 12, 12)}px rgba(0, 0, 0, 0.15)`,
                }}
              />
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchBar;